import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { LinearProgress, GridLegacy as Grid } from '@mui/material';
import DevicesDataTable from '../elements/DevicesDataTable';
import TimeRangeFilterPopover, {
  TimeRangeOptions,
} from '../elements/filter/TimeRangeFilterPopover';
import getBuildingStaging from '../../../redux/selectors/staging';
import TextFilterPopover from '../elements/filter/TextFilterPopover';
import StagingFilterBuilder, { FilterDefinition } from '../elements/StagingFilterBuilder';
import BuildingStagingLoader from '../../loaders/BuildingStagingLoader';
import EquipmentLoader from '../../loaders/EquipmentLoader';
import { DeploymentsLoader } from '../../loaders/deployments';
import LocationLoader from '../../loaders/LocationLoader';
import { ContainsTextFilter, TimeRangeFilter } from '../../../redux/constants/filters';
import { FilterProvider } from '../contexts/FilterContext';
import { setPage } from '../../../redux/actions/staging';
import { Page, Row } from '../../util/Page';

const DevicesView = ({ buildingId, loaded, devices, points, resetPage }) => {
  useEffect(() => resetPage, [resetPage]);

  const filterDefs = useMemo(
    () => [
      new FilterDefinition(
        TextFilterPopover,
        'device_id',
        'Device ID',
        {},
        (v) =>
          new ContainsTextFilter({
            key: 'device_id',
            getter: (d) => d.device_id,
            fragment: v,
          })
      ),
      new FilterDefinition(
        TextFilterPopover,
        'device_name',
        'Device Name',
        {},
        (v) =>
          new ContainsTextFilter({
            key: 'device_name',
            getter: (d) => d.name,
            fragment: v,
          })
      ),
      new FilterDefinition(
        TextFilterPopover,
        'device_description',
        'Description',
        {},
        (v) =>
          new ContainsTextFilter({
            key: 'device_description',
            getter: (d) => d.properties?.description,
            fragment: v,
          })
      ),
      new FilterDefinition(
        TimeRangeFilterPopover,
        'first_discovered_time',
        'First Discovered Time',
        new TimeRangeOptions(
          devices
            ?.values()
            .map((d) => Date.parse(d.created))
            ?.reduce((acc, val) => (acc === null ? val : Math.min(acc, val)), null) ?? 0,
          // eslint-disable-next-line react-hooks/purity
          Date.now()
        ),
        ({ from, to }) =>
          new TimeRangeFilter({
            key: 'first_discovered_time',
            getter: (device) => Date.parse(device.created),
            minimum: from,
            maximum: to,
          })
      ),
      new FilterDefinition(
        TimeRangeFilterPopover,
        'last_discovered_time',
        'Latest Discovered Time',
        new TimeRangeOptions(
          devices
            ?.values()
            .map((d) => Date.parse(d.last_discovery))
            ?.reduce((acc, val) => (acc === null ? val : Math.min(acc, val)), null) ?? 0,
          // eslint-disable-next-line react-hooks/purity
          Date.now()
        ),
        ({ from, to }) =>
          new TimeRangeFilter({
            key: 'last_discovered_time',
            getter: (device) => Date.parse(device.last_discovery),
            minimum: from,
            maximum: to,
          })
      ),
    ],
    [devices]
  );

  if (!loaded) {
    return (
      <Page>
        <Row>
          <BuildingStagingLoader key={`stg-${buildingId}`} buildingId={buildingId} />
          <EquipmentLoader key={buildingId} buildingId={buildingId} />
          <LocationLoader key={`ll-${buildingId}`} buildingId={buildingId} />
          <DeploymentsLoader key="deployments" />
          <LinearProgress color="secondary" className="loading" />
        </Row>
      </Page>
    );
  }

  return (
    <FilterProvider initialValue={[]} onFilterChange={resetPage}>
      <Grid item xs={12}>
        <StagingFilterBuilder filterDefs={filterDefs} />
      </Grid>
      <Grid item xs={12}>
        <DevicesDataTable buildingId={buildingId} devices={devices} points={points} />
      </Grid>
    </FilterProvider>
  );
};

const mapStateToProps = (state, { buildingId }) => ({
  ...getBuildingStaging(state, buildingId),
  loaded: state.entities.staging.loaded[buildingId] ?? false,
  buildingId,
});

const mapDispatchToProps = (dispatch) => ({
  resetPage: () => dispatch(setPage(0)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DevicesView);
